import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@ludus/ui'
import type { AuditsApiError } from '../../api/index.ts'
import { useRunStage } from '../../hooks/index.ts'
import type { AuditState, StageSummary } from '../../types.ts'

interface StagesPanelProps {
  auditId: string
  state: AuditState
  stages: StageSummary[]
  enabled: string[]
  onChanged: () => Promise<void>
}

const RUNNABLE: readonly AuditState[] = ['processing', 'ready', 'failed']

/**
 * The stages in run order with state and done/total; Run, Retry failed and
 * Run on N post `scope ∈ all | failed | ids[]` with `limit` (§10.6). The
 * snapshot has no Run control — it is never restartable.
 */
export function StagesPanel({ auditId, state, stages, enabled, onChanged }: StagesPanelProps) {
  const { t } = useTranslation()
  const { trigger, isMutating } = useRunStage(auditId)
  const [limits, setLimits] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string | null>(null)
  const runnable = RUNNABLE.includes(state)

  async function run(stage: string, scope: 'all' | 'failed', limit?: number) {
    setFailure(null)
    try {
      await trigger({ stage, scope, ...(limit === undefined ? {} : { limit }) })
      await onChanged()
    } catch (err) {
      const error = err as AuditsApiError
      setFailure(
        t(`pages.audits.stagesPanel.refused.${error.code ?? 'generic'}`, {
          defaultValue: error.message,
        }),
      )
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('pages.audits.stagesPanel.title', { defaultValue: 'Stages' })}</CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-2">
        {failure && (
          <p role="alert" className="text-xs text-toast-error-text">
            {failure}
          </p>
        )}
        <ul className="flex flex-col divide-y divide-border/60">
          {stages.map((row) => {
            const isEnabled = row.enabled && enabled.includes(row.stage)
            const failed = row.units.failed + row.units.needs_review
            const limit = Number(limits[row.stage] ?? '')
            return (
              <li key={row.stage} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <div className="flex min-w-[10rem] flex-col">
                  <span className="font-medium text-foreground">
                    {t(`pages.audits.stages.${row.stage}`, { defaultValue: row.stage })}
                  </span>
                  <span className="text-[11px] text-muted">
                    {t(`pages.audits.stagesPanel.level.${row.level}`, { defaultValue: row.level })}
                  </span>
                </div>
                <StageState row={row} enabled={isEnabled} />
                {row.level === 'card' && (
                  <span className="text-xs text-muted">
                    {t('pages.audits.stagesPanel.doneOfTotal', {
                      defaultValue: '{{done}} / {{total}} done',
                      done: row.units.done,
                      total: row.units.total,
                    })}
                  </span>
                )}
                {Object.entries(row.skipped).map(([reason, n]) => (
                  <Badge key={reason} tone="muted" size="sm">
                    {t('pages.audits.stagesPanel.skipped', {
                      defaultValue: '{{n}} skipped · {{reason}}',
                      n,
                      reason: t(`pages.audits.skipReasons.${reason}`, { defaultValue: reason }),
                    })}
                  </Badge>
                ))}
                {row.stage !== 'snapshot' && (
                  <div className="ml-auto flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!runnable || !isEnabled || isMutating}
                      onClick={() => void run(row.stage, 'all')}
                    >
                      {t('pages.audits.stagesPanel.run', { defaultValue: 'Run' })}
                    </Button>
                    {row.level === 'card' && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!runnable || !isEnabled || isMutating || failed === 0}
                          onClick={() => void run(row.stage, 'failed')}
                        >
                          {t('pages.audits.stagesPanel.retryFailed', {
                            defaultValue: 'Retry failed ({{n}})',
                            n: failed,
                          })}
                        </Button>
                        <Input
                          type="number"
                          min={1}
                          className="w-20"
                          aria-label={t('pages.audits.stagesPanel.limitLabel', {
                            defaultValue: 'N cards',
                          })}
                          value={limits[row.stage] ?? ''}
                          onChange={(e) => setLimits({ ...limits, [row.stage]: e.target.value })}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!runnable || !isEnabled || isMutating || !(limit >= 1)}
                          onClick={() => void run(row.stage, 'all', limit)}
                        >
                          {t('pages.audits.stagesPanel.runOn', { defaultValue: 'Run on N' })}
                        </Button>
                      </>
                    )}
                  </div>
                )}
                {row.stage === 'snapshot' && (
                  <span className="ml-auto text-[11px] text-muted">
                    {t('pages.audits.stagesPanel.snapshotOnce', {
                      defaultValue: 'Taken once at creation; never restarted.',
                    })}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      </CardBody>
    </Card>
  )
}

function StageState({ row, enabled }: { row: StageSummary; enabled: boolean }) {
  const { t } = useTranslation()
  if (!enabled) {
    return (
      <Badge tone="muted" size="sm">
        {t('pages.audits.stagesPanel.state.disabled', { defaultValue: 'disabled' })}
      </Badge>
    )
  }
  if (row.units.running > 0 || row.job?.state === 'running' || row.job?.state === 'queued') {
    return (
      <Badge tone="primary" size="sm">
        {t('pages.audits.stagesPanel.state.running', { defaultValue: 'running' })}
      </Badge>
    )
  }
  if (row.units.queued > 0) {
    return (
      <Badge tone="info" size="sm">
        {t('pages.audits.stagesPanel.state.queued', { defaultValue: 'queued' })}
      </Badge>
    )
  }
  if (row.units.failed + row.units.needs_review > 0 || row.job?.state === 'failed') {
    return (
      <Badge tone="warning" size="sm">
        {t('pages.audits.stagesPanel.state.attention', { defaultValue: 'needs attention' })}
      </Badge>
    )
  }
  if (row.units.done > 0 || row.job?.state === 'done') {
    return (
      <Badge tone="success" size="sm">
        {t('pages.audits.stagesPanel.state.done', { defaultValue: 'done' })}
      </Badge>
    )
  }
  return (
    <Badge tone="default" size="sm">
      {t('pages.audits.stagesPanel.state.pending', { defaultValue: 'not run' })}
    </Badge>
  )
}
